// SessionsMonitorAndAnalysis.jsx
import React, { useState, useEffect } from 'react';
import SessionsTabs from './components/SessionsTabs';
import JsonViewer from './components/JsonViewer';
import JsonModal from './components/JsonModal';
import TimeSeriesAnalysis from './components/TimeSeriesAnalysis';
import TextMarkersAnalysis from './components/TextMarkersAnalysis';
import TextAnomalityAnalysis from './components/TextAnomalityAnalysis';
import ActivityAnalysis from './components/ActivityAnalysis';
import './SessionsMonitorAndAnalysis.scss';
import { getAnalyzeActAPIUrl, getAPIUrl } from "../api/apiUtils";
import { formatRuDate, formatRuDateTime } from '../utils/ruDateUtils';
import { apiFetch } from "../api/apiClient";

const API_BASE_URL = getAPIUrl();

const API_URL = `${API_BASE_URL}/monitoring/sessions`;

const statusOptions = [
  { value: '', label: 'All Statuses' },
  { value: 2, label: 'Pending', color: 'status-pending' },
  { value: 1, label: 'Completed', color: 'status-completed' },
  { value: -1, label: 'Failed', color: 'status-failed' },
  { value: 0, label: 'Running', color: 'status-running' }
];

const SessionsTable = () => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });
  const [filters, setFilters] = useState({
    job_type: '',
    status: '',
    group_id: '',
    meta_id: '',
    date_range: ''
  });
  const [expandedRow, setExpandedRow] = useState(null);
  const [jobTypeOptions, setJobTypeOptions] = useState([{ value: '', label: 'All Types' }]);
  const [jsonModal, setJsonModal] = useState({ isOpen: false, data: null, title: '' });

  useEffect(() => {
    fetchSessions();
    fetchJobTypes();
  }, [currentPage, sortConfig, filters]);

  const fetchJobTypes = async () => {
    try {
      const response = await apiFetch(`${API_BASE_URL}/monitoring/job-types`);
      if (response.ok) {
        const jobTypes = await response.json();
        const options = [
          { value: '', label: 'All Types' },
          ...jobTypes.map(type => ({
            value: type,
            label: type.charAt(0).toUpperCase() + type.slice(1)
          }))
        ];
        setJobTypeOptions(options);
      }
    } catch (err) {
      console.error('Ошибка при загрузке типов задач:', err);
    }
  };

  const fetchSessions = async () => {
    try {
      setLoading(true);
      setError(null);
      const params = new URLSearchParams({
        _page: currentPage,
        _limit: itemsPerPage,
        _sort: sortConfig.key,
        _order: sortConfig.direction,
        ...(filters.job_type && { job_type: filters.job_type }),
        ...(filters.status && { status: filters.status }),
        ...(filters.group_id && { group_id: filters.group_id }),
        ...(filters.meta_id && { meta_id: filters.meta_id }),
        ...(filters.date_range && { date_range: filters.date_range })
      });
      const response = await apiFetch(`${API_URL}?${params.toString()}`);
      if (!response.ok) throw new Error('Failed to fetch sessions');
      const data = await response.json();
      setSessions(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
    setSortConfig({ key, direction });
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
    setCurrentPage(1);
  };

  const handleDateRangeChange = (range) => {
    setFilters(prev => ({ ...prev, date_range: range }));
    setCurrentPage(1);
  };

  const toggleRowExpand = (sessionId) => {
    setExpandedRow(expandedRow === sessionId ? null : sessionId);
  };

  const openJsonModal = (data, title) => {
    setJsonModal({ isOpen: true, data, title });
  };

  const closeJsonModal = () => {
    setJsonModal({ isOpen: false, data: null, title: '' });
  };

  const handleRetry = async (sessionId) => {
    try {
      const response = await apiFetch(`${API_URL.replace('/sessions', '')}/sessions/${sessionId}/retry`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      const data = await response.json();
      if (response.ok) {
        alert(`Информация о сессии ${sessionId}:\nСтатус: ${data.current_status}\nПримечание: ${data.note}`);
      } else {
        alert(`Ошибка: ${data.error}`);
      }
    } catch (err) {
      alert(`Ошибка при получении информации: ${err.message}`);
    }
  };

  const formatDate = (dateString) => formatRuDateTime(dateString);

  const getStatusBadge = (status) => {
    const statusOption = statusOptions.find(opt => opt.value === status.toString());
    if (!statusOption) return null;
    return <span className={`status-badge ${statusOption.color}`}>{statusOption.label}</span>;
  };

  const renderSortIcon = (key) => {
    if (sortConfig.key !== key) return null;
    return <span className="sort-icon">{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>;
  };

  const renderJson = (jsonString, title = "JSON Data") => {
    return <JsonViewer data={jsonString} title={title} />;
  };

  return (
    <>
      <div className="card">
        <div className="card-header">
          <h2 className="card-title">
            <i className="fas fa-table icon-left"></i>
            Табличное представление
          </h2>
          <p className="card-subtitle">Детальный просмотр всех сессий с фильтрацией и сортировкой</p>
        </div>

        <div className="filter-section">
          <div className="filter-grid">
            <div className="filter-group">
              <label className="filter-label">Job Type</label>
              <select
                name="job_type"
                value={filters.job_type}
                onChange={handleFilterChange}
                className="filter-select"
              >
                {jobTypeOptions.map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
            <div className="filter-group">
              <label className="filter-label">Status</label>
              <select
                name="status"
                value={filters.status}
                onChange={handleFilterChange}
                className="filter-select"
              >
                {statusOptions.map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
            <div className="filter-group">
              <label className="filter-label">Group ID</label>
              <input
                type="text"
                name="group_id"
                value={filters.group_id}
                onChange={handleFilterChange}
                placeholder="Filter by group"
                className="filter-input"
              />
            </div>
            <div className="filter-group">
              <label className="filter-label">Meta ID</label>
              <input
                type="text"
                name="meta_id"
                value={filters.meta_id}
                onChange={handleFilterChange}
                placeholder="Filter by meta"
                className="filter-input"
              />
            </div>
          </div>
          <div className="date-range-buttons">
            {['today', 'week', 'month', ''].map(range => (
              <button
                key={range || 'all'}
                onClick={() => handleDateRangeChange(range)}
                className={`date-range-btn ${filters.date_range === range ? 'active' : ''}`}
              >
                {range === 'today' ? 'Today' :
                 range === 'week' ? 'This Week' :
                 range === 'month' ? 'This Month' : 'All Time'}
              </button>
            ))}
          </div>
        </div>

        {loading && (
          <div className="loading-container">
            <div className="loading-spinner">
              <i className="fas fa-circle-notch fa-2x"></i>
            </div>
            <span className="loading-text">Loading sessions...</span>
          </div>
        )}

        {error && (
          <div className="error-state error-message">
            <i className="fas fa-exclamation-triangle"></i>
            {error}
            <button onClick={fetchSessions} className="btn btn-primary ml-4">
              Retry
            </button>
          </div>
        )}

        {!loading && !error && (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th onClick={() => handleSort('session_id')} className="sortable">ID {renderSortIcon('session_id')}</th>
                  <th onClick={() => handleSort('job_type')} className="sortable">Job Type {renderSortIcon('job_type')}</th>
                  <th>Group ID / Name</th>
                  <th>Meta ID</th>
                  <th onClick={() => handleSort('start_date')} className="sortable">Date Range {renderSortIcon('start_date')}</th>
                  <th onClick={() => handleSort('created_at')} className="sortable">Created At {renderSortIcon('created_at')}</th>
                  <th onClick={() => handleSort('status')} className="sortable">Status {renderSortIcon('status')}</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sessions.map(session => (
                  <React.Fragment key={session.session_id}>
                    <tr className="table-row">
                      <td>{session.session_id}</td>
                      <td><span className="capitalize">{session.job_type}</span></td>
                      <td>
                        <div>
                          <span className="font-medium">{session.group_id || '-'}</span>
                          <span className="text-muted">{session.group_name || '-'}</span>
                        </div>
                      </td>
                      <td>{session.meta_id || '-'}</td>
                      <td>
                        {session.start_date ? formatRuDate(session.start_date) : '-'} - {session.end_date ? formatRuDate(session.end_date) : '-'}
                      </td>
                      <td>{formatDate(session.created_at)}</td>
                      <td>{getStatusBadge(session.status)}</td>
                      <td>
                        <button onClick={() => toggleRowExpand(session.session_id)} className="btn-icon">
                          {expandedRow === session.session_id ? <i className="fas fa-eye-slash"></i> : <i className="fas fa-eye"></i>}
                        </button>
                        <button onClick={() => handleRetry(session.session_id)} className="btn-icon" title="Просмотр информации о сессии (только чтение)">
                          <i className="fas fa-info-circle"></i>
                        </button>
                      </td>
                    </tr>
                    {expandedRow === session.session_id && (
                      <tr>
                        <td colSpan="8" className="expanded-row">
                          <div className="expanded-content">
                            <div>
                              <div className="expanded-header">
                                <h3>Session Details</h3>
                                <button onClick={() => openJsonModal(session.details, "Session Details")} className="btn btn-sm btn-primary">
                                  <i className="fas fa-external-link-alt"></i> Open Full View
                                </button>
                              </div>
                              {renderJson(session.details, "Session Details")}
                            </div>
                            <div>
                              <div className="expanded-header">
                                <h3>Session Parameters</h3>
                                <button onClick={() => openJsonModal(session.params, "Session Parameters")} className="btn btn-sm btn-success">
                                  <i className="fas fa-external-link-alt"></i> Open Full View
                                </button>
                              </div>
                              {renderJson(session.params, "Session Parameters")}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>

            <div className="pagination">
              <p className="pagination-info">
                Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, sessions.length)} of {sessions.length} results
              </p>
              <nav className="pagination-nav">
                <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1} className="pagination-btn">
                  <i className="fas fa-angle-double-left"></i>
                </button>
                <button onClick={() => setCurrentPage(Math.max(currentPage - 1, 1))} disabled={currentPage === 1} className="pagination-btn">
                  <i className="fas fa-angle-left"></i>
                </button>
                {Array.from({ length: 3 }, (_, i) => {
                  const page = currentPage - 1 + i;
                  if (page < 1) return null;
                  return (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`pagination-btn ${currentPage === page ? 'active' : ''}`}
                    >
                      {page}
                    </button>
                  );
                })}
                <button onClick={() => setCurrentPage(currentPage + 1)} className="pagination-btn">
                  <i className="fas fa-angle-right"></i>
                </button>
                <button onClick={() => setCurrentPage(currentPage + 1)} className="pagination-btn">
                  <i className="fas fa-angle-double-right"></i>
                </button>
              </nav>
            </div>
          </div>
        )}

        {!loading && !error && (
          <div className="stats-grid">
            <div className="stat-card stat-total">
              <div className="stat-content">
                <div className="stat-header">
                  <h3>Всего сессий</h3>
                  <div className="stat-icon-circle">
                    <i className="fas fa-database"></i>
                  </div>
                </div>
                <div className="stat-value">{sessions.length}</div>
              </div>
            </div>
            <div className="stat-card stat-completed">
              <div className="stat-content">
                <div className="stat-header">
                  <h3>Завершено</h3>
                  <div className="stat-icon-circle">
                    <i className="fas fa-check"></i>
                  </div>
                </div>
                <div className="stat-value">{sessions.filter(s => s.status === 1).length}</div>
              </div>
            </div>
            <div className="stat-card stat-failed">
              <div className="stat-content">
                <div className="stat-header">
                  <h3>Неудачно</h3>
                  <div className="stat-icon-circle">
                    <i className="fas fa-times"></i>
                  </div>
                </div>
                <div className="stat-value">{sessions.filter(s => s.status === -1).length}</div>
              </div>
            </div>
            <div className="stat-card stat-running">
              <div className="stat-content">
                <div className="stat-header">
                  <h3>Выполняется</h3>
                  <div className="stat-icon-circle">
                    <i className="fas fa-sync-alt"></i>
                  </div>
                </div>
                <div className="stat-value">{sessions.filter(s => s.status === 0).length}</div>
              </div>
            </div>
            <div className="stat-card stat-pending">
              <div className="stat-content">
                <div className="stat-header">
                  <h3>Ожидает</h3>
                  <div className="stat-icon-circle">
                    <i className="fas fa-clock"></i>
                  </div>
                </div>
                <div className="stat-value">{sessions.filter(s => s.status === 2).length}</div>
              </div>
            </div>
          </div>
        )}
      </div>

      <JsonModal isOpen={jsonModal.isOpen} onClose={closeJsonModal} data={jsonModal.data} title={jsonModal.title} />
    </>
  );
};

const AnalysisTabs = () => {
  const [activeTab, setActiveTab] = useState('centrality');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const analysisTabs = [
    { id: 'centrality', label: 'Центральность', icon: 'fas fa-bullseye', description: 'Анализ центральности узлов в сети' },
    { id: 'activity', label: 'Активность', icon: 'fas fa-chart-line', description: 'Анализ активности участников' },
    { id: 'neural', label: 'Нейросеть', icon: 'fas fa-brain', description: 'Результаты нейросетевого анализа' },
    { id: 'text_markers', label: 'Расчет текстовых маркеров', icon: 'fas fa-tags', description: 'Анализ текстовых маркеров' },
    { id: 'topic_intensity', label: 'Анализ интенсивности тематик', icon: 'fas fa-fire', description: 'Анализ интенсивности обсуждения тем' },
    { id: 'anomaly', label: 'Анализ аномальности дискуссий', icon: 'fas fa-exclamation-triangle', description: 'Выявление аномальных паттернов в дискуссиях' },
    { id: 'time_series', label: 'Анализ временных рядов', icon: 'fas fa-clock', description: 'Временной анализ данных' },
    { id: 'srl', label: 'SRL', icon: 'fas fa-cogs', description: 'Анализ SRL (Self-Regulated Learning)' }
  ];

  const renderAnalysisContent = (tabId) => {
    const tab = analysisTabs.find(t => t.id === tabId);
    if (tabId === 'time_series') return <TimeSeriesAnalysis />;
    if (tabId === 'text_markers') return <TextMarkersAnalysis />;
    if (tabId === 'anomaly') return <TextAnomalityAnalysis />;
    if (tabId === 'activity') return <ActivityAnalysis />;

    // Реальные данные из существующих результатов
    const sampleData = [
      {
        id: '001',
        name: 'Пример результата 1',
        value: '0.75',
        status: 'Успешно'
      },
      {
        id: '002', 
        name: 'Пример результата 2',
        value: '0.92',
        status: 'В процессе'
      }
    ];

    return (
      <div className="analysis-content">
        <div className="analysis-header">
          <i className={`${tab.icon} icon-large ${tab.id}-color`}></i>
          <h3>{tab.label}</h3>
          <p>{tab.description}</p>
        </div>
        <div className="analysis-stats">
          <div className="stat-item">
            <div className="stat-icon"><i className={`${tab.icon} ${tab.id}-color`}></i></div>
            <div className="stat-value">0.85</div>
            <div className="stat-label">Общий показатель</div>
          </div>
          <div className="stat-item">
            <div className="stat-icon"><i className="fas fa-chart-bar text-green"></i></div>
            <div className="stat-value">92%</div>
            <div className="stat-label">Успешность</div>
          </div>
          <div className="stat-item">
            <div className="stat-icon"><i className="fas fa-users text-blue"></i></div>
            <div className="stat-value">1,247</div>
            <div className="stat-label">Участников</div>
          </div>
          <div className="stat-item">
            <div className="stat-icon"><i className="fas fa-clock text-orange"></i></div>
            <div className="stat-value">2.3ч</div>
            <div className="stat-label">Время анализа</div>
          </div>
          <div className="stat-item">
            <div className="stat-icon"><i className="fas fa-database text-purple"></i></div>
            <div className="stat-value">45.2K</div>
            <div className="stat-label">Обработано записей</div>
          </div>
        </div>
        <div className="chart-placeholder">
          <i className={`${tab.icon} icon-placeholder ${tab.id}-color`}></i>
          <p>График для {tab.label.toLowerCase()}</p>
          <p className="placeholder-note">Данные будут загружены здесь</p>
        </div>
        <div className="results-table">
          <table className="table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Название</th>
                <th>Значение</th>
                <th>Статус</th>
              </tr>
            </thead>
            <tbody>
              {sampleData.map((item, index) => (
                <tr key={index}>
                  <td>{item.id}</td>
                  <td>{item.name}</td>
                  <td>{item.value}</td>
                  <td>
                    <span className={`status-badge ${item.status === 'Успешно' ? 'status-completed' : 'status-pending'}`}>
                      {item.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="additional-info">
          <div className="info-section">
            <h4>Дополнительная информация</h4>
            <p>Здесь отображаются дополнительные данные и метрики для анализа {tab.label.toLowerCase()}.</p>
            <div className="info-grid">
              <div className="info-item">
                <strong>Последнее обновление:</strong> {formatRuDateTime(new Date())}
              </div>
              <div className="info-item">
                <strong>Следующее обновление:</strong> {formatRuDateTime(new Date(Date.now() + 24 * 60 * 60 * 1000))}
              </div>
              <div className="info-item">
                <strong>Версия алгоритма:</strong> v2.1.3
              </div>
              <div className="info-item">
                <strong>Точность модели:</strong> 94.2%
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="card">
      <div className="card-header">
        <h2 className="card-title">
          <i className="fas fa-chart-bar icon-left"></i>
          Результаты анализа
        </h2>
        <p className="card-subtitle">Аналитические данные и отчеты по сессиям</p>
      </div>
      <div className="tab-nav">
        {analysisTabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`tab-btn ${activeTab === tab.id ? 'active' : ''}`}
          >
            <i className={tab.icon}></i>
            {tab.label}
          </button>
        ))}
      </div>
      <div className="tab-content">
        {loading && (
          <div className="loading-overlay">
            <div className="loading-spinner text-green"><i className="fas fa-circle-notch fa-2x"></i></div>
            <p className="loading-text">Загрузка данных анализа...</p>
          </div>
        )}
        {error && (
          <div className="error-message">
            <i className="fas fa-exclamation-triangle"></i>
            {error}
            <button onClick={() => setError(null)} className="btn btn-success ml-4">Повторить</button>
          </div>
        )}
        {!loading && !error && renderAnalysisContent(activeTab)}
      </div>
    </div>
  );
};

const SessionsMonitorAndAnalysis = () => {
  const [activeSection, setActiveSection] = useState('monitoring');
  const [activeView, setActiveView] = useState('tabs');

  const navigationItems = [
    { id: 'monitoring', label: 'Мониторинг сессий', icon: 'fas fa-chart-line' },
    { id: 'analysis', label: 'Результаты анализа', icon: 'fas fa-chart-bar' }
  ];

  const renderMonitoringContent = () => (
    <div className="view-switcher">
      <div className="view-header">
        <h3>Режим просмотра</h3>
        <p>Выберите способ отображения данных мониторинга</p>
      </div>
      <div className="view-buttons">
        <button
          onClick={() => setActiveView('tabs')}
          className={`view-btn ${activeView === 'tabs' ? 'active' : ''}`}
        >
          <i className="fas fa-tabs"></i> Вкладки
        </button>
        <button
          onClick={() => setActiveView('original')}
          className={`view-btn ${activeView === 'original' ? 'active' : ''}`}
        >
          <i className="fas fa-table"></i> Таблица
        </button>
      </div>
      {activeView === 'tabs' ? <SessionsTabs /> : <SessionsTable />}
    </div>
  );

  const renderAnalysisContent = () => <AnalysisTabs />;

  return (
    <div className="app-container">      
      <nav className="main-nav">
        {navigationItems.map(item => (
          <button
            key={item.id}
            onClick={() => setActiveSection(item.id)}
            className={`nav-btn ${activeSection === item.id ? 'active' : ''}`}
          >
            <i className={item.icon}></i>
            {item.label}
          </button>
        ))}
      </nav>
      <main className="main-content">
        {activeSection === 'monitoring' ? renderMonitoringContent() : renderAnalysisContent()}
      </main>
    </div>
  );
};

export default SessionsMonitorAndAnalysis;